import { Updatable, WebSide } from '../core/types';

const OVERLAY_HTML = `
  <h1>WEB <span>SWINGER</span></h1>
  <p>Swing across a cartoon city. Sling webs onto buildings and let momentum carry you over the
     streets, cars and crowds below — or run straight into a wall to <b>stick and climb it</b>,
     then vault over the edge onto the rooftops.</p>
  <div class="keys">
    <div><b>Mouse</b> Look around</div>
    <div><b>L-Click</b> Left-hand web</div>
    <div><b>W A S D</b> Run / air-steer</div>
    <div><b>R-Click</b> Right-hand web</div>
    <div><b>Space</b> Jump / leap off wall</div>
    <div><b>Shift</b> Reel in web</div>
    <div><b>Wall</b> Run into it to climb</div>
    <div><b>W/S</b> Up·down · <b>A/D</b> sideways</div>
  </div>
  <div id="loading">loading city…</div>
  <button id="play" style="display:none">SWING</button>
  <div id="err"></div>
`;

/**
 * Owns every DOM/HUD element and exposes a small, intent-revealing API. No game
 * system pokes the DOM directly — they call methods like `setSpeed` or
 * `showHint`, which keeps presentation cleanly separated from simulation.
 */
export class Hud implements Updatable {
  private readonly crosshair = el('div', 'cross');
  private readonly speed = el('div', undefined, 'speed');
  private readonly stats = el('div', undefined, 'stats');
  private readonly pillL = pill();
  private readonly pillR = pill();
  private readonly hint = el('div', 'hint');
  private readonly overlay = el('div', undefined, 'overlay');
  private readonly title: HTMLElement;
  private readonly playButton: HTMLButtonElement;
  private readonly loading: HTMLElement;

  private hintTimer = 0;

  constructor() {
    this.crosshair.id = 'cross';
    document.body.appendChild(this.crosshair);

    const hud = el('div', undefined, 'hud');
    hud.append(this.speed, this.stats);
    document.body.appendChild(hud);

    const webState = el('div', undefined, 'webstate');
    webState.append(text('L '), this.pillL, text(' R '), this.pillR);
    document.body.appendChild(webState);

    document.body.appendChild(this.hint);

    this.overlay.innerHTML = OVERLAY_HTML;
    document.body.appendChild(this.overlay);
    this.title = this.overlay.querySelector('h1')!;
    this.playButton = this.overlay.querySelector('#play')!;
    this.loading = this.overlay.querySelector('#loading')!;

    this.setSpeed(0);
    this.stats.textContent = 'altitude 0 m';
  }

  onPlay(listener: () => void): void {
    this.playButton.addEventListener('click', listener);
  }

  /** Swap the instructions for touch devices. */
  enableTouchMode(): void {
    const paragraph = this.overlay.querySelector('p');
    if (paragraph) {
      paragraph.innerHTML =
        'Swing across a cartoon city. Use the <b>left joystick</b> to move, <b>drag the right side</b> ' +
        'to look, and tap the <b>🕸️ buttons</b> to sling webs. Move into a wall to <b>climb it</b>, ' +
        'then vault onto the rooftops.';
    }
    const keys = this.overlay.querySelector('.keys');
    if (keys) {
      keys.innerHTML = `
        <div><b>Joystick</b> Move / climb</div>
        <div><b>🕸️ L / R</b> Shoot webs</div>
        <div><b>Drag R</b> Look around</div>
        <div><b>JUMP</b> Jump / leap off wall</div>
        <div><b>REEL</b> Reel in web</div>
        <div><b>Wall</b> Move into it to climb</div>`;
    }
  }

  markReady(): void {
    this.loading.style.display = 'none';
    this.playButton.style.display = 'inline-block';
  }

  showError(message: string): void {
    const err = this.overlay.querySelector('#err') as HTMLElement;
    err.style.display = 'block';
    err.textContent = message;
    this.loading.style.display = 'none';
  }

  hideOverlay(): void {
    this.overlay.classList.add('hidden');
  }

  showPaused(): void {
    this.overlay.classList.remove('hidden');
    this.title.innerHTML = 'PAUSED';
    this.playButton.textContent = 'RESUME';
    this.playButton.style.display = 'inline-block';
    this.loading.style.display = 'none';
  }

  setPointerLocked(locked: boolean): void {
    this.crosshair.classList.toggle('locked', locked);
  }

  setWebActive(side: WebSide, active: boolean): void {
    const p = side === 'L' ? this.pillL : this.pillR;
    p.classList.toggle('on', active);
    p.textContent = active ? '●' : '○';
    this.crosshair.style.transform = `translate(-50%,-50%) scale(${active ? 0.55 : 0.65})`;
  }

  setSpeed(kmh: number): void {
    this.speed.innerHTML = `${kmh}<small> km/h</small>`;
  }

  setStats(altitude: number, mode: string): void {
    this.stats.textContent = `altitude ${altitude} m${mode ? ` · ${mode}` : ''}`;
  }

  showHint(message: string, seconds = 4): void {
    this.hint.textContent = message;
    this.hint.style.opacity = '1';
    this.hintTimer = seconds;
  }

  update(dt: number): void {
    if (this.hintTimer > 0) {
      this.hintTimer -= dt;
      if (this.hintTimer <= 0) this.hint.style.opacity = '0';
    }
  }
}

function el(tag: string, className?: string, id?: string): HTMLElement {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (id) node.id = id;
  return node;
}

function pill(): HTMLElement {
  const p = el('span', 'pill');
  p.textContent = '○';
  return p;
}

function text(value: string): Text {
  return document.createTextNode(value);
}
